import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SSOApiService from "../../../services/SsoServices";
import { SSO_CONTENT_LIST } from "../../../constants/api-constants";
import ContentCard from "./ContentCard";
import "./purchase.css";
/*istanbul ignore next */
const CatalogContentList = () => {
  const { catalogId } = useParams();
  const history = useHistory();
  const [catalogData, setCatalogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCatalogContents();
  }, [catalogId]);

  const loadCatalogContents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await SSOApiService.ssoPost(SSO_CONTENT_LIST, {
        pageNumber: 1,
        pageSize: 1000,
        ignorePaging: true,
        searchText: "",
        filter: {
          contentType: [],
          typeOfContent: [],
          typeOfForm: [],
          lineOfBusiness: [],
          countries: [],
          states: [],
          contentFillable: [],
          userIds: [],
          selectedColumns: [],
          selectedContentOriginators: [],
          contentOriginator: "",
          subscriberId: 0,
        },
      });

      if (response?.result?.records) {
        const catalog = response.result.records.find((item) => {
          return item.contentId === Number.parseInt(catalogId);
        });
        if (catalog) {
          setCatalogData({
            name: catalog.name,
            documentType: catalog.documentType,
            contents: normalizeContents(catalog.contents || []),
          });
        } else {
          setError(
            `Catalog or Bundle with ID ${catalogId} not found. Please check if the item exists or try refreshing the page.`,
          );
        }
      } else {
        setError("Failed to load contents");
      }
    } catch (err) {
      console.error("Error loading catalog contents:", err);
      setError("An error occurred while loading contents");
    } finally {
      setLoading(false);
    }
  };

  const normalizeContents = (contents) => {
    return contents.map((content) => {
      const subscriptionTypeMap = {
        1: "Monthly",
        2: "Quarterly",
        3: "Semi-Annual",
        4: "Annual",
        99: "One Time",
      };

      // Priority order for displaying subscription types
      const priorityOrder = [99, 1, 2, 3, 4];

      const priceItems = [];

      // Add one-time cost if available
      if (content.oneTimeCost && content.oneTimeCost > 0) {
        priceItems.push({
          type: 99,
          value: content.oneTimeCost,
          label: "One Time",
          isRecurring: false,
        });
      }

      // Collect prices from subscriptions array
      if (content.subscriptions && content.subscriptions.length > 0) {
        content.subscriptions.forEach((sub) => {
          if (sub.status === 1 && (sub.actualValue > 0 || sub.baseValue > 0)) {
            const value = sub.actualValue || sub.baseValue;
            const label = subscriptionTypeMap[sub.frequency] || "Unknown";
            priceItems.push({
              type: sub.frequency,
              value: value,
              label: label,
              isRecurring: sub.isRecurring || false,
              contentSubscriptionId: sub.contentSubscriptionId,
            });
          }
        });
      }

      // Check if content is free (no prices collected)
      const isFree = priceItems.length === 0;

      // Sort by priority order
      priceItems.sort((a, b) => {
        const indexA = priorityOrder.indexOf(a.type);
        const indexB = priorityOrder.indexOf(b.type);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      // Format price for display (only first item)
      const formatPrice = () => {
        if (isFree) return "$0";
        const firstItem = priceItems[0];
        return `$${firstItem.value} – ${firstItem.label}`;
      };

      const firstSub = content.subscriptions?.[0];
      return {
        id: content.contentId,
        formNumber: content.formNumber,
        latestVersion: content.latestVersion,
        name: content.name,
        type: content.typeOfContent,
        typeOfContent: content.typeOfContent,
        brand: content.brandName,
        distributor: content.userName,
        version: content.latestVersion,
        isPreview: content.isPreview,
        previewType: content.previewType,
        isLink: !!content.webLink,
        url: content.webLink || content.uniqueFileName,
        price: formatPrice(),
        subscriptionOptions: priceItems, // All available subscriptions
        defaultSubscription: priceItems[0] || null, // First one (shown on card)
        recurring: firstSub?.isRecurring,
        isPurchased: firstSub?.isAssociated,
        isAddedInCart: content.isAddedInCart || false,
        documentType: content.documentType,
        totalContent: content.totalContent || 0,
        contents: content.contents || [],
      };
    });
  };

  if (loading) {
    return (
      <div className="purchase-wrapper">
        <div
          className="sso-loader-wrapper"
          style={{ padding: "20px", color: "white" }}
        >
          <div className="sso-loader" />
          Loading Contents...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="purchase-wrapper">
        <div className="header-with-tabs">
          <div className="header">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton
                onClick={() => history.goBack()}
                style={{ color: "#fff" }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
              <h2 className="purchase-title">Content</h2>
            </div>
          </div>
        </div>
        <div style={{ padding: "20px", textAlign: "center", color: "#f0ebeb" }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!catalogData) {
    return (
      <div className="purchase-wrapper">
        <div className="header-with-tabs">
          <div className="header">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton
                onClick={() => history.goBack()}
                style={{ color: "#fff" }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
              <h2 className="purchase-title">Content</h2>
            </div>
          </div>
        </div>
        <div style={{ padding: "20px", textAlign: "center", color: "#f0ebeb" }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-wrapper">
      <div className="header-with-tabs">
        <div className="header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <IconButton
              onClick={() => history.goBack()}
              style={{ color: "#fff" }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <h2 className="purchase-title">
              <img
                src={
                  catalogData.documentType === 3
                    ? "/images/Bundle@2x.png"
                    : "/images/Catalog@2x.png"
                }
                alt={catalogData.documentType === 3 ? "Bundle" : "Catalog"}
                style={{
                  width: "24px",
                  height: "24px",
                  marginRight: "8px",
                  verticalAlign: "middle",
                  marginBottom: "6px",
                }}
              />
              {catalogData.name}
            </h2>
          </div>
        </div>
      </div>

      <div className="content-scroll-area">
        <div className="content-list">
          {catalogData.contents.length === 0 ? (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#f0ebeb" }}
            >
              No contents available in this{" "}
              {catalogData.documentType === 3 ? "bundle" : "catalog"}
            </div>
          ) : (
            catalogData.contents.map((item) => (
              <ContentCard key={item.id} item={item} hideCart={true} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogContentList;
